import React, { useState, useEffect } from 'react';
import { Outlet, Link, useLocation, Navigate } from 'react-router-dom';
import { 
  LayoutDashboard, 
  Map, 
  Zap, 
  Smartphone, 
  AlertTriangle, 
  Calendar,
  History,
  Radio,
  ClipboardList,
  Settings,
  Shield,
  LogOut,
  Menu,
  X,
  Sun,
  Moon,
  User,
  BookOpen
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import BrandLogo from './BrandLogo';
import LanguageSwitcher from './LanguageSwitcher';
import ImpersonationBanner from './ImpersonationBanner';
import {
  AWS_NAVIGATION_PATHS,
  FRONTEND_MODE_BADGE,
  IS_AWS_MODE,
} from '../config/frontend';

interface NavItem {
  nameKey: string;
  descKey: string;
  href: string;
  icon: React.ElementType;
}

const navigation: NavItem[] = [
  { nameKey: 'nav:dashboard', descKey: 'nav:dashboardDesc', href: '/dashboard', icon: LayoutDashboard },
  { nameKey: 'nav:map', descKey: 'nav:mapDesc', href: '/map', icon: Map },
  { nameKey: 'nav:zones', descKey: 'nav:zonesDesc', href: '/zones', icon: Zap },
  { nameKey: 'nav:devices', descKey: 'nav:devicesDesc', href: '/devices', icon: Smartphone },
  { nameKey: 'nav:alerts', descKey: 'nav:alertsDesc', href: '/alerts', icon: AlertTriangle },
  { nameKey: 'nav:schedules', descKey: 'nav:schedulesDesc', href: '/schedules', icon: Calendar },
  { nameKey: 'nav:irrigationHistory', descKey: 'nav:irrigationHistoryDesc', href: '/irrigation-history', icon: History },
  { nameKey: 'nav:iot', descKey: 'nav:iotDesc', href: '/iot', icon: Radio },
  { nameKey: 'nav:workOrders', descKey: 'nav:workOrdersDesc', href: '/work-orders', icon: ClipboardList },
  { nameKey: 'nav:settings', descKey: 'nav:settingsDesc', href: '/settings', icon: Settings },
  { nameKey: 'nav:documentation', descKey: 'nav:documentationDesc', href: '/documentation', icon: BookOpen },
];

const visibleNavigation = IS_AWS_MODE
  ? navigation.filter((item) => (AWS_NAVIGATION_PATHS as readonly string[]).includes(item.href))
  : navigation;

const Layout: React.FC = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { user, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const { t } = useTranslation(['common', 'nav']);
  const location = useLocation();
  const [impersonatingUser, setImpersonatingUser] = useState<any>(null);

  // Check for impersonation data
  useEffect(() => {
    if (IS_AWS_MODE) return;
    const impersonationData = localStorage.getItem('impersonating');
    if (impersonationData) {
      try {
        setImpersonatingUser(JSON.parse(impersonationData));
      } catch (error) {
        console.error('Error parsing impersonation data:', error);
      }
    }
  }, []);

  const handleStopImpersonation = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/impersonate/revert`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        localStorage.setItem('token', data.access_token);
        localStorage.removeItem('impersonating');
        setImpersonatingUser(null);
        window.location.href = '/admin/users';
      }
    } catch (error) {
      console.error('Failed to stop impersonation:', error);
    }
  };

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  const currentPage = visibleNavigation.find(item => item.href === location.pathname);

  return (
    <div className="min-h-screen bg-[var(--bg-primary)]">
      {/* Impersonation Banner */}
      {!IS_AWS_MODE && impersonatingUser && (
        <ImpersonationBanner
          targetUser={impersonatingUser}
          onStopImpersonation={handleStopImpersonation}
        />
      )}
      
      {/* Mobile sidebar backdrop */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={`
        fixed top-0 left-0 z-50 h-full w-64 bg-[var(--surface)] border-r border-[var(--border-light)] shadow-lg
        transform transition-transform duration-300 ease-in-out lg:translate-x-0
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
      `}>
        {/* Sidebar Header */}
        <div className="flex items-center justify-between h-16 px-6 border-b border-[var(--border-light)]">
          <BrandLogo size="md" showText={true} />
          <button
            onClick={() => setSidebarOpen(false)}
            className="lg:hidden p-1 rounded-md hover:bg-[var(--gray-100)]"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* User Info */}
        <div className="px-6 py-4 border-b border-[var(--border-light)]">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-gradient-primary rounded-full flex items-center justify-center">
              <User className="w-5 h-5 text-white" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-[var(--text-primary)] truncate">{user.name}</p>
              <p className="text-xs text-[var(--text-secondary)] truncate">{user.role}</p>
            </div>
          </div>
          <span className={`inline-flex mt-3 px-2 py-1 rounded-full text-xs font-medium ${
            IS_AWS_MODE
              ? 'bg-green-100 text-green-700'
              : 'bg-amber-100 text-amber-700'
          }`}>
            {FRONTEND_MODE_BADGE}
          </span>
        </div>

        {/* Navigation */}
        <nav className="flex-1 px-4 py-4 space-y-2">
          {/* Regular Navigation */}
          {visibleNavigation.map((item) => {
            const isActive = location.pathname === item.href;
            return (
              <Link
                key={item.nameKey}
                to={item.href}
                onClick={() => setSidebarOpen(false)}
                className={`
                  group flex items-center px-3 py-2.5 text-sm font-medium rounded-lg transition-all duration-200
                  ${isActive 
                    ? 'bg-gradient-primary text-white shadow-md' 
                    : 'text-[var(--text-secondary)] hover:bg-[var(--gray-100)] hover:text-[var(--text-primary)]'
                  }
                `}
                data-testid={`nav-${item.nameKey.toLowerCase().replace(' ', '-')}`}
              >
                <item.icon className={`
                  w-5 h-5 mr-3 transition-colors
                  ${isActive ? 'text-white' : 'text-[var(--text-tertiary)] group-hover:text-[var(--text-primary)]'}
                `} />
                <div className="flex-1">
                  <div className="font-medium">{t(item.nameKey)}</div>
                  <div className={`text-xs mt-0.5 ${isActive ? 'text-white/80' : 'text-[var(--text-tertiary)]'}`}>
                    {t(item.descKey)}
                  </div>
                </div>
              </Link>
            );
          })}

          {/* Admin Navigation (SuperAdmin only) */}
          {!IS_AWS_MODE && user?.role === 'superadmin' && (
            <>
              <div className="my-4 border-t border-[var(--border-light)]"></div>
              <Link
                to="/admin"
                onClick={() => setSidebarOpen(false)}
                className={`
                  group flex items-center px-3 py-2.5 text-sm font-medium rounded-lg transition-all duration-200
                  ${location.pathname.startsWith('/admin')
                    ? 'bg-red-500 text-white shadow-md' 
                    : 'text-[var(--text-secondary)] hover:bg-red-50 hover:text-red-600'
                  }
                `}
                data-testid="nav-admin"
              >
                <Shield className={`
                  w-5 h-5 mr-3 transition-colors
                  ${location.pathname.startsWith('/admin') ? 'text-white' : 'text-red-500 group-hover:text-red-600'}
                `} />
                <div className="flex-1">
                  <div className="font-medium">{t('nav:admin')}</div>
                  <div className={`text-xs mt-0.5 ${location.pathname.startsWith('/admin') ? 'text-white/80' : 'text-[var(--text-tertiary)]'}`}>
                    {t('nav:adminDesc')}
                  </div>
                </div>
              </Link>
            </>
          )}
        </nav>

        {/* Sidebar Footer */}
        <div className="px-4 py-4 border-t border-[var(--border-light)]">
          <div className="space-y-3">
            <LanguageSwitcher />
            <div className="flex items-center justify-between">
              <button
                onClick={toggleTheme}
                className="p-2 rounded-lg hover:bg-[var(--gray-100)] transition-colors"
                data-testid="theme-toggle"
                title={t('nav:theme')}
              >
                {theme === 'light' ? (
                  <Moon className="w-5 h-5 text-[var(--text-secondary)]" />
                ) : (
                  <Sun className="w-5 h-5 text-[var(--text-secondary)]" />
                )}
              </button>
              <button
                onClick={logout}
                className="flex items-center px-3 py-2 text-sm font-medium text-[var(--text-secondary)] hover:text-[var(--color-danger)] hover:bg-red-50 rounded-lg transition-colors"
                data-testid="logout-button"
              >
                <LogOut className="w-4 h-4 mr-2" />
                {t('nav:logout')}
              </button>
            </div>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <div className="lg:ml-64">
        {/* Top Header */}
        <header className="bg-[var(--surface)] border-b border-[var(--border-light)] px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <button
                onClick={() => setSidebarOpen(true)}
                className="lg:hidden p-2 rounded-md hover:bg-[var(--gray-100)] mr-4"
                data-testid="mobile-menu-button"
              >
                <Menu className="w-5 h-5" />
              </button>
              <div>
                <h1 className="text-heading-3 text-[var(--text-primary)]">
                  {currentPage ? t(currentPage.nameKey) : t('appName')}
                </h1>
                {currentPage && (
                  <p className="text-body-sm text-[var(--text-secondary)] mt-1">
                    {t(currentPage.descKey)}
                  </p>
                )}
              </div>
            </div>
            
            <div className="flex items-center space-x-4">
              <span className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${
                IS_AWS_MODE
                  ? 'bg-green-100 text-green-700'
                  : 'bg-amber-100 text-amber-700'
              }`}>
                {FRONTEND_MODE_BADGE}
              </span>
              <div className="hidden sm:block text-right">
                <p className="text-sm font-medium text-[var(--text-primary)]">{user.name}</p>
                <p className="text-xs text-[var(--text-secondary)] capitalize">{user.role}</p>
              </div>
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main className="p-6">
          <div className="max-w-7xl mx-auto">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
};

export default Layout;
